import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { CardIcon } from '@components/Pages/AnalyticsPage/CardIcon';
import { ProfessionChip } from '@components/ui/ProfessionChip/ProfessionChip';
import type { IOptionChip } from '@components/Pages/AnalyticsPage/analytics.types';
import classes from './SummaryStats.module.css';

interface ISummaryStatsProps {
  tradeLabel: string;
  /**
   * Optional chip metadata (background hex + tradeId + display text) for
   * the leading "Trade" card. When provided, the chip renders with the
   * profession's actual `backgroundColor` and trade icon — matching
   * trusstor's `ProfessionDisplayWithTradeIcon`. When absent the chip
   * falls back to a neutral cyan tint + helmet glyph so the strip still
   * renders during the brief window before the profession catalog lands.
   */
  tradeChip?: IOptionChip | null;
  workingSinceLabel: string;
  totalHours: number;
  avgWorkers: number;
  avgHours: number;
}

/**
 * Hero strip that sits beneath the page title. Five cards, left → right:
 *   1. Trade chip (per-trade icon + profession-tinted background)
 *   2. Working Since (calendar icon + "May 2026")
 *   3. Total Work Hours (clock icon + "421h")
 *   4. Average workers per day (helmet icon + decimal)
 *   5. Average hours per day (clock icon + decimal)
 *
 * `memo`-wrapped so the strip skips re-render when unrelated query state in
 * the page (e.g. `analysisQuery` settling) churns. Callers must pass stable
 * `tradeChip` references — the page memoizes the chip on `matchedProfession`.
 */
export const SummaryStats = memo(SummaryStatsImpl);

function SummaryStatsImpl(props: ISummaryStatsProps) {
  const { t } = useTranslation();
  const { tradeLabel, tradeChip, workingSinceLabel, totalHours, avgWorkers, avgHours } = props;
  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <span className={classes.label}>
          {t('reports.contractorAnalysis.summary.trade')}
        </span>
        {tradeChip ? (
          <span className={classes.tradeChipWrap}>
            <ProfessionChip chip={tradeChip} showContractor={false} />
          </span>
        ) : (
          <span className={classes.tradeChip}>
            <CardIcon name="helmet" size={16} />
            {tradeLabel}
          </span>
        )}
      </div>
      <Card
        label={t('reports.contractorAnalysis.summary.workingSince')}
        icon="calendar"
        value={workingSinceLabel}
      />
      <Card
        label={t('reports.contractorAnalysis.summary.totalWorkHours')}
        icon="clock"
        value={t('reports.contractorAnalysis.summary.hoursUnit', { n: totalHours })}
      />
      <Card
        label={t('reports.contractorAnalysis.summary.avgWorkersPerDay')}
        icon="helmet"
        value={avgWorkers.toFixed(2)}
      />
      <Card
        label={t('reports.contractorAnalysis.summary.avgHoursPerDay')}
        icon="clock"
        value={avgHours.toFixed(2)}
      />
    </div>
  );
}

interface ICardProps {
  label: string;
  icon: 'calendar' | 'clock' | 'helmet';
  value: string;
}

function Card({ label, icon, value }: ICardProps) {
  return (
    <div className={classes.card}>
      <span className={classes.label}>{label}</span>
      <div className={classes.valueRow}>
        <span className={classes.iconCircle}>
          <CardIcon name={icon} size={18} />
        </span>
        <span className={classes.value}>{value}</span>
      </div>
    </div>
  );
}
